
import React from 'react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Users, Target, Zap, Clock, CheckCircle, Award } from 'lucide-react';

const About = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-gray-900 dark:to-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white">
                About Pixel Tools
              </h1>
              <p className="mt-4 text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                We're on a mission to help developers and entrepreneurs build better products faster.
              </p>
            </div>
          </div>
        </section>

        {/* Our Story */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-8">Our Story</h2>
              <div className="prose dark:prose-invert max-w-none">
                <p className="text-lg mb-4">
                  Pixel Tools was founded in 2023 by a group of passionate developers who were frustrated with the fragmented nature of developer tools and resources.
                </p>
                <p className="text-lg mb-4">
                  We noticed that while there were many incredible products being built by independent developers and small teams, they often struggled to get the visibility they deserved. At the same time, other developers were spending countless hours searching for the right tools to solve their problems.
                </p>
                <p className="text-lg mb-4">
                  That's why we created Pixel Tools — a platform to connect developers with the best tools, resources, and knowledge they need to build amazing products.
                </p>
                <p className="text-lg">
                  Today, our platform features hundreds of carefully selected products and resources, helping thousands of developers and entrepreneurs build better, faster.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Our Values */}
        <section className="py-16 bg-gray-50 dark:bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">Our Values</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
                <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center mb-4">
                  <Target className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Quality First</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  We carefully select and review every product featured on our platform to ensure it meets our quality standards.
                </p>
              </div>
              
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
                <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center mb-4">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Community Driven</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  We believe in the power of community and actively involve our users in shaping the platform's future.
                </p>
              </div>
              
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
                <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center mb-4">
                  <Zap className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Innovation</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  We're always on the lookout for innovative new tools and approaches that can help developers work smarter.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">Meet Our Team</h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { name: "Alex Chen", role: "Founder & CEO", image: "/placeholder.svg" },
                { name: "Sarah Miller", role: "CTO", image: "/placeholder.svg" },
                { name: "David Kim", role: "Head of Product", image: "/placeholder.svg" },
                { name: "Emma Wilson", role: "Head of Content", image: "/placeholder.svg" }
              ].map((member, index) => (
                <div key={index} className="text-center">
                  <div className="w-40 h-40 mx-auto rounded-full overflow-hidden mb-4">
                    <img 
                      src={member.image} 
                      alt={member.name} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="text-xl font-semibold">{member.name}</h3>
                  <p className="text-gray-600 dark:text-gray-400">{member.role}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 bg-primary">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-5xl font-bold text-white mb-2">500+</div>
                <p className="text-purple-200">Products Featured</p>
              </div>
              <div>
                <div className="text-5xl font-bold text-white mb-2">50k+</div>
                <p className="text-purple-200">Monthly Visitors</p>
              </div>
              <div>
                <div className="text-5xl font-bold text-white mb-2">100+</div>
                <p className="text-purple-200">Blog Articles</p>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to join our community?</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
              Discover the best tools and resources to help you build amazing products.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <button className="button-primary">
                Explore Products
              </button>
              <button className="button-primary bg-white dark:bg-gray-800 text-primary border border-primary hover:bg-gray-50">
                Read the Blog
              </button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default About;
